import React, { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Loader2, LogOut, Save, ExternalLink, Plus, Trash2, GripVertical, Users, Image as ImageIcon, Layout, Phone, Trophy, HeartHandshake, ShoppingBag, Mail, Package, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import ImagePicker from "./ImagePicker";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Soft "ding" played on new paid orders. Synthesized via Web Audio so we
// don't ship an audio asset and so the browser's autoplay policy is satisfied
// after the owner's first interaction (login click counts).
function playDing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [880, 1320].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      const start = now + i * 0.12;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.22, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start(start);
      o.stop(start + 0.4);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch (_) { /* sound is non-critical */ }
}

const TABS = [
  { id: "hero", label: "Hero", icon: Layout },
  { id: "awards", label: "Awards", icon: Trophy },
  { id: "combos", label: "Combos", icon: ShoppingBag },
  { id: "pickup", label: "Pickup Menu", icon: Package },
  { id: "orders", label: "Orders", icon: Receipt },
  { id: "community", label: "Community", icon: HeartHandshake },
  { id: "footer", label: "Footer", icon: Phone },
  { id: "messages", label: "Messages", icon: Mail },
  { id: "team", label: "Team", icon: Users },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [active, setActive] = useState("hero");
  const [content, setContent] = useState(null);
  const [combos, setCombos] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [saving, setSaving] = useState(false);
  const [unseenOrders, setUnseenOrders] = useState(0);
  const prevUnseenRef = React.useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [c, cm, ad] = await Promise.all([
          axios.get(`${API}/site/content`),
          axios.get(`${API}/site/combos`),
          axios.get(`${API}/admin/users`),
        ]);
        setContent(c.data);
        setCombos(cm.data);
        setAdmins(ad.data);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [user]);

  // Poll for new paid orders. Ding + toast when the count goes up.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/orders/unseen-count`);
        if (cancelled) return;
        const next = data?.count ?? 0;
        const prev = prevUnseenRef.current;
        if (next > prev) {
          playDing();
          const newOnes = next - prev;
          toast.success(`${newOnes} new paid order${newOnes > 1 ? "s" : ""}`, {
            description: "Open the Orders tab to fulfill.",
          });
        }
        prevUnseenRef.current = next;
        setUnseenOrders(next);
      } catch (err) {
        // Network blips while polling are non-blocking — log for ops visibility
        // but don't toast (would be noisy on every flaky tick).
        if (process.env.NODE_ENV !== "production") {
          console.debug("[admin] unseen-count poll failed", err);
        }
      }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // Mark orders as seen when the owner opens the Orders tab.
  useEffect(() => {
    if (active !== "orders" || unseenOrders === 0) return;
    (async () => {
      try {
        await axios.post(`${API}/admin/orders/mark-seen`);
        prevUnseenRef.current = 0;
        setUnseenOrders(0);
      } catch (err) {
        // Marking seen is a best-effort UX nicety — log silently and move on.
        if (process.env.NODE_ENV !== "production") {
          console.debug("[admin] mark-seen failed", err);
        }
      }
    })();
  }, [active, unseenOrders]);

  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!content) return <FullScreenLoader />;

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, content);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0B0B0B]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/brand/btw-logo.png" alt="BTW" className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-white p-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-base sm:text-lg uppercase tracking-tight truncate">BTW Admin</div>
              <div className="text-[10px] sm:text-xs text-white/55 truncate">{user.email} · {user.role}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-white/70 hover:text-white">
              View Site <ExternalLink size={12} />
            </a>
            <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] border border-white/15 hover:bg-white/10 text-xs font-bold uppercase tracking-[0.14em] px-3 py-2">
              <LogOut size={13} /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>

        {/* Tab strip */}
        <div className="max-w-6xl mx-auto px-2 sm:px-6 pb-2 overflow-x-auto no-scrollbar">
          <div className="flex gap-1.5">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = active === t.id;
              const showBadge = t.id === "orders" && unseenOrders > 0;
              return (
                <button key={t.id} onClick={() => setActive(t.id)} data-testid={`admin-tab-${t.id}`}
                  className={`relative shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.16em] border transition-colors ${isActive ? "bg-[#5FBDD6] text-[#0B0B0B] border-[#5FBDD6]" : "bg-white/[0.04] text-white/75 border-white/10 hover:border-white/25"}`}>
                  <Icon size={13} /> {t.label}
                  {showBadge && (
                    <span
                      data-testid="admin-orders-unseen-badge"
                      className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-black tracking-normal animate-pulse"
                      title={`${unseenOrders} new paid order${unseenOrders > 1 ? "s" : ""}`}
                    >
                      {unseenOrders > 9 ? "9+" : unseenOrders}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-32">
        {active === "hero" && <HeroEditor value={content.hero} onChange={(v) => setContent({ ...content, hero: v })} />}
        {active === "awards" && <AwardsEditor value={content.awards} onChange={(v) => setContent({ ...content, awards: v })} />}
        {active === "community" && <CommunityEditor value={content.community} onChange={(v) => setContent({ ...content, community: v })} />}
        {active === "footer" && <FooterEditor value={content.footer} links={content.links} onChange={(v, l) => setContent({ ...content, footer: v, links: l ?? content.links })} />}
        {active === "combos" && <CombosEditor combos={combos} setCombos={setCombos} />}
        {active === "pickup" && <PickupItemsEditor />}
        {active === "orders" && <OrdersPanel />}
        {active === "messages" && <MessagesPanel />}
        {active === "team" && <TeamEditor admins={admins} setAdmins={setAdmins} currentUserId={user.user_id} />}
      </main>

      {/* Sticky save bar for content tabs */}
      {["hero","awards","community","footer"].includes(active) && (
        <div className="fixed bottom-0 inset-x-0 z-30 bg-[#0B0B0B]/95 backdrop-blur border-t border-white/10 px-4 py-3 sm:px-6">
          <div className="max-w-6xl mx-auto flex items-center justify-end">
            <button onClick={saveContent} disabled={saving} data-testid="admin-save-content"
              className="inline-flex items-center gap-2 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] disabled:opacity-60 text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-6 py-3 text-sm transition-colors">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FullScreenLoader() {
  return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B]"><Loader2 className="animate-spin text-white" size={28} /></div>;
}

// ---------- Reusable inputs ----------
function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold tracking-[0.2em] uppercase text-white/65 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-[10px] text-white/40">{hint}</span>}
    </label>
  );
}
const Input = (p) => <input {...p} className={`w-full rounded-lg bg-white/[0.06] border border-white/15 text-white px-3 py-2.5 placeholder:text-white/30 focus:outline-none focus:border-[#5FBDD6] ${p.className||""}`} />;
const Textarea = (p) => <textarea {...p} className={`w-full rounded-lg bg-white/[0.06] border border-white/15 text-white px-3 py-2.5 placeholder:text-white/30 focus:outline-none focus:border-[#5FBDD6] ${p.className||""}`} />;

function SectionCard({ title, children }) {
  return (
    <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 sm:p-6 mb-5">
      <h2 className="font-display text-xl sm:text-2xl uppercase tracking-tight mb-4">{title}</h2>
      <div className="grid gap-4">{children}</div>
    </section>
  );
}

// ---------- HERO ----------
function HeroEditor({ value, onChange }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const badges = v.badges || [];
  const setBadge = (i, patch) => {
    const next = badges.slice();
    next[i] = { ...next[i], ...patch };
    set({ badges: next });
  };
  return (
    <>
      <SectionCard title="Hero headline">
        <Field label="Line 1"><Input value={v.headline_line1 || ""} onChange={(e) => set({ headline_line1: e.target.value })} data-testid="hero-line1" /></Field>
        <Field label="Line 2 (calypso accent)"><Input value={v.headline_line2 || ""} onChange={(e) => set({ headline_line2: e.target.value })} data-testid="hero-line2" /></Field>
        <Field label="Line 3"><Input value={v.headline_line3 || ""} onChange={(e) => set({ headline_line3: e.target.value })} data-testid="hero-line3" /></Field>
        <Field label="Subtext" hint="Use a line break for the second line"><Textarea rows={3} value={v.subtext || ""} onChange={(e) => set({ subtext: e.target.value })} data-testid="hero-subtext" /></Field>
      </SectionCard>
      <SectionCard title="Hero photo">
        <ImagePicker value={v.image} onChange={(p) => set({ image: p })} folder="hero" />
      </SectionCard>
      <SectionCard title="Trust badges">
        {badges.map((b, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white/[0.03] border border-white/10 rounded-xl p-3">
            <Field label={`Badge ${i + 1} title`}><Input value={b.title || ""} onChange={(e) => setBadge(i, { title: e.target.value })} /></Field>
            <Field label="Subtitle"><Input value={b.sub || ""} onChange={(e) => setBadge(i, { sub: e.target.value })} /></Field>
          </div>
        ))}
      </SectionCard>
    </>
  );
}

// ---------- AWARDS ----------
function AwardsEditor({ value, onChange }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <>
      <SectionCard title="Awards copy">
        <Field label="Eyebrow"><Input value={v.eyebrow || ""} onChange={(e) => set({ eyebrow: e.target.value })} /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Headline · before"><Input value={v.headline_pre || ""} onChange={(e) => set({ headline_pre: e.target.value })} /></Field>
          <Field label="Highlight (calypso)"><Input value={v.headline_hl || ""} onChange={(e) => set({ headline_hl: e.target.value })} /></Field>
          <Field label="Headline · after"><Input value={v.headline_post || ""} onChange={(e) => set({ headline_post: e.target.value })} /></Field>
        </div>
        <Field label="Subtext"><Input value={v.subtext || ""} onChange={(e) => set({ subtext: e.target.value })} /></Field>
        <Field label="Body"><Textarea rows={3} value={v.body || ""} onChange={(e) => set({ body: e.target.value })} /></Field>
        <Field label="Accent line"><Input value={v.accent || ""} onChange={(e) => set({ accent: e.target.value })} /></Field>
      </SectionCard>
      <SectionCard title="Award photo">
        <ImagePicker value={v.image} onChange={(p) => set({ image: p })} folder="awards" />
      </SectionCard>
    </>
  );
}

// ---------- COMMUNITY ----------
function CommunityEditor({ value, onChange }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const photos = v.photos || [];
  const setPhoto = (i, patch) => {
    const next = photos.slice();
    next[i] = { ...next[i], ...patch };
    set({ photos: next });
  };
  return (
    <>
      <SectionCard title="Community copy">
        <Field label="Eyebrow"><Input value={v.eyebrow || ""} onChange={(e) => set({ eyebrow: e.target.value })} /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Headline · prefix"><Input value={v.headline_pre || ""} onChange={(e) => set({ headline_pre: e.target.value })} /></Field>
          <Field label="Highlight"><Input value={v.headline_hl || ""} onChange={(e) => set({ headline_hl: e.target.value })} /></Field>
        </div>
        <Field label="Lead (the bold sentence)"><Textarea rows={2} value={v.lead || ""} onChange={(e) => set({ lead: e.target.value })} /></Field>
        <Field label="Body"><Textarea rows={3} value={v.body || ""} onChange={(e) => set({ body: e.target.value })} /></Field>
        <Field label="Quote callout"><Input value={v.quote || ""} onChange={(e) => set({ quote: e.target.value })} /></Field>
      </SectionCard>
      <SectionCard title="Staff photos">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {photos.map((p, i) => (
            // Positional CMS slot — order never changes at runtime.
            <div key={`staff-photo-${i}`} className="bg-white/[0.03] border border-white/10 rounded-xl p-3 grid gap-2">
              <ImagePicker value={p.src} onChange={(src) => setPhoto(i, { src })} folder="staff" />
              <Field label={`Alt text · photo ${i + 1}`}><Input value={p.alt || ""} onChange={(e) => setPhoto(i, { alt: e.target.value })} /></Field>
            </div>
          ))}
        </div>
      </SectionCard>
    </>
  );
}

// ---------- FOOTER ----------
function FooterEditor({ value, links, onChange }) {
  const v = value || {};
  const l = links || {};
  const setF = (patch) => onChange({ ...v, ...patch }, l);
  const setL = (patch) => onChange(v, { ...l, ...patch });
  const hours = v.hours || [];
  const setHour = (i, patch) => {
    const next = hours.slice();
    next[i] = { ...next[i], ...patch };
    setF({ hours: next });
  };
  return (
    <>
      <SectionCard title="Contact info">
        <Field label="Address line 1"><Input value={v.address_line1 || ""} onChange={(e) => setF({ address_line1: e.target.value })} /></Field>
        <Field label="Address line 2"><Input value={v.address_line2 || ""} onChange={(e) => setF({ address_line2: e.target.value })} /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Phone (display)"><Input value={v.phone || ""} onChange={(e) => setF({ phone: e.target.value })} /></Field>
          <Field label="Tel (digits for tel:)"><Input value={v.tel || ""} onChange={(e) => setF({ tel: e.target.value })} /></Field>
        </div>
        <Field label="Email"><Input type="email" value={v.email || ""} onChange={(e) => setF({ email: e.target.value })} /></Field>
      </SectionCard>
      <SectionCard title="Hours">
        {hours.map((h, i) => (
          // Hours row — positional CMS slot, day-of-week stays in slot order.
          <div key={`hours-${i}`} className="grid grid-cols-2 gap-3">
            <Input value={h.days || ""} onChange={(e) => setHour(i, { days: e.target.value })} placeholder="Days" />
            <Input value={h.time || ""} onChange={(e) => setHour(i, { time: e.target.value })} placeholder="Time or 'Closed'" />
          </div>
        ))}
      </SectionCard>
      <SectionCard title="Social & links">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Instagram URL"><Input value={v.instagram || ""} onChange={(e) => setF({ instagram: e.target.value })} /></Field>
          <Field label="Facebook URL"><Input value={v.facebook || ""} onChange={(e) => setF({ facebook: e.target.value })} /></Field>
          <Field label="TikTok URL"><Input value={v.tiktok || ""} onChange={(e) => setF({ tiktok: e.target.value })} /></Field>
          <Field label="IG / FB handle"><Input value={v.ig_handle || ""} onChange={(e) => setF({ ig_handle: e.target.value })} /></Field>
          <Field label="TikTok handle"><Input value={v.tiktok_handle || ""} onChange={(e) => setF({ tiktok_handle: e.target.value })} /></Field>
        </div>
        <Field label="Order Now URL (Grubhub etc.)"><Input value={l.order_url || ""} onChange={(e) => setL({ order_url: e.target.value })} /></Field>
        <Field label="Catering URL"><Input value={l.catering_url || ""} onChange={(e) => setL({ catering_url: e.target.value })} /></Field>
      </SectionCard>
    </>
  );
}

// ---------- COMBOS CRUD (with drag-reorder) ----------
function SortableComboRow({ combo, onSave, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: combo.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 grid gap-3"
      data-testid={`combo-edit-${combo.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          aria-label="Drag to reorder"
          data-testid={`combo-drag-${combo.id}`}
          className="inline-flex items-center gap-1.5 text-white/40 hover:text-white/90 cursor-grab active:cursor-grabbing touch-none select-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em]">id: {combo.id}</span>
        </button>
        <button onClick={() => onRemove(combo.id)} className="text-red-400 hover:text-red-300" data-testid={`combo-delete-${combo.id}`}>
          <Trash2 size={16} />
        </button>
      </div>
      <ImagePicker value={combo.img} onChange={(p) => onSave(combo.id, { img: p })} folder="combos" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name"><Input value={combo.name || ""} onChange={(e) => onSave(combo.id, { name: e.target.value })} /></Field>
        <Field label="Price"><Input value={combo.price || ""} onChange={(e) => onSave(combo.id, { price: e.target.value })} /></Field>
        <Field label="Description"><Input value={combo.desc || ""} onChange={(e) => onSave(combo.id, { desc: e.target.value })} /></Field>
        <Field label="Badge (Best Value / Most Popular / blank)"><Input value={combo.badge || ""} onChange={(e) => onSave(combo.id, { badge: e.target.value })} /></Field>
      </div>
    </div>
  );
}

function CombosEditor({ combos, setCombos }) {
  const [busy, setBusy] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const save = async (id, patch) => {
    const next = combos.map((c) => (c.id === id ? { ...c, ...patch } : c));
    setCombos(next);
    try {
      await axios.put(`${API}/admin/combos/${id}`, next.find((c) => c.id === id));
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const addCombo = async () => {
    setBusy(true);
    const draft = { name: "New Combo", desc: "", price: "$0.00", badge: "", img: "", order: combos.length };
    try {
      const { data } = await axios.post(`${API}/admin/combos`, draft);
      setCombos([...combos, data]);
      toast.success("Added");
    } finally {
      setBusy(false);
    }
  };

  const removeCombo = async (id) => {
    if (!confirm("Delete this combo?")) return;
    await axios.delete(`${API}/admin/combos/${id}`);
    setCombos(combos.filter((c) => c.id !== id));
    toast.success("Deleted");
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = combos.findIndex((c) => c.id === active.id);
    const newIdx = combos.findIndex((c) => c.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const next = arrayMove(combos, oldIdx, newIdx).map((c, i) => ({ ...c, order: i }));
    setCombos(next);
    try {
      await axios.put(`${API}/admin/combos-reorder`, { ids: next.map((c) => c.id) });
      toast.success("Reordered");
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl uppercase tracking-tight">Popular Combos</h2>
        <button onClick={addCombo} disabled={busy} data-testid="combo-add"
          className="inline-flex items-center gap-2 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase text-xs px-4 py-2">
          <Plus size={14} /> Add
        </button>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={combos.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          <div className="grid gap-4" data-testid="combos-sortable-list">
            {combos.map((c) => (
              <SortableComboRow key={c.id} combo={c} onSave={save} onRemove={removeCombo} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <p className="text-xs text-white/40">Drag the <GripVertical size={11} className="inline align-text-bottom" /> handle to reorder. Edits auto-save when you tab away.</p>
    </div>
  );
}

// ---------- TEAM (admin users) ----------
function TeamEditor({ admins, setAdmins, currentUserId }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/users`, {
        email, name, password: password || null, role: "admin",
      });
      setAdmins([...admins, data]);
      setEmail(""); setName(""); setPassword("");
      toast.success(`Invited ${data.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (uid) => {
    if (!confirm("Remove this admin? They'll be signed out.")) return;
    try {
      await axios.delete(`${API}/admin/users/${uid}`);
      setAdmins(admins.filter((a) => a.user_id !== uid));
      toast.success("Removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="grid gap-5">
      <SectionCard title="Invite an admin">
        <form onSubmit={invite} className="grid gap-3 sm:grid-cols-2">
          <Field label="Email"><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" /></Field>
          <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" /></Field>
          <div className="sm:col-span-2">
            <Field label="Password (optional · leave blank for Google-login only)" hint="If set, they can also sign in with email + password">
              <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="•••••••• (optional)" data-testid="invite-password" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={busy} data-testid="invite-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] disabled:opacity-60 text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-6 py-3 text-sm">
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
              Invite
            </button>
          </div>
        </form>
      </SectionCard>

      <SectionCard title="Current admins">
        <ul className="divide-y divide-white/10">
          {admins.map((a) => (
            <li key={a.user_id} className="py-3 flex items-center justify-between gap-3" data-testid={`admin-row-${a.user_id}`}>
              <div className="min-w-0 flex items-center gap-3">
                <span className="inline-flex h-9 w-9 rounded-full bg-white/10 items-center justify-center font-display uppercase text-sm shrink-0">
                  {(a.name || a.email)[0]?.toUpperCase()}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-bold truncate">{a.name || a.email}</div>
                  <div className="text-xs text-white/55 truncate">{a.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">{a.role}</span>
                {a.user_id !== currentUserId && (
                  <button onClick={() => remove(a.user_id)} className="text-red-400 hover:text-red-300" data-testid={`admin-remove-${a.user_id}`}>
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}


// ---------- MESSAGES (catering inquiries, contact, newsletter) ----------
const MSG_TABS = [
  { id: "catering", label: "Catering", endpoint: "catering" },
  { id: "contact", label: "Contact", endpoint: "contact" },
  { id: "newsletter", label: "Newsletter", endpoint: "newsletter" },
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function MessagesPanel() {
  const [active, setActive] = useState("catering");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (kind) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/messages/${kind}`);
      setRows(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(active); }, [active]);

  const remove = async (id) => {
    if (!confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API}/admin/messages/${active}/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-2xl uppercase tracking-tight">Messages</h2>
        <div className="flex gap-1.5">
          {MSG_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              data-testid={`messages-subtab-${t.id}`}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.16em] border transition-colors ${
                active === t.id
                  ? "bg-[#5FBDD6] text-[#0B0B0B] border-[#5FBDD6]"
                  : "bg-white/[0.04] text-white/75 border-white/10 hover:border-white/25"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <SectionCard title={`${MSG_TABS.find((t) => t.id === active).label} · ${rows.length}`}>
        {loading ? (
          <div className="flex items-center gap-2 text-white/60 text-sm">
            <Loader2 className="animate-spin" size={14} /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-white/50">No entries yet.</p>
        ) : (
          <ul className="divide-y divide-white/10" data-testid={`messages-list-${active}`}>
            {rows.map((r) => (
              <li key={r.id} className="py-3 flex items-start justify-between gap-3" data-testid={`message-row-${r.id}`}>
                <div className="min-w-0 grow text-sm">
                  {active === "catering" && (
                    <>
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="font-bold">{r.name}</span>
                        <a href={`mailto:${r.email}`} className="text-[#5FBDD6] hover:underline">{r.email}</a>
                        {r.phone && <a href={`tel:${r.phone}`} className="text-white/65">· {r.phone}</a>}
                      </div>
                      <div className="text-xs text-white/55 mt-0.5">
                        {r.event_date && <>Event: {r.event_date}{r.event_time ? ` @ ${r.event_time}` : ""} · </>}
                        {r.guests && <>Guests: {r.guests} · </>}
                        {r.staffing && <>Service: {r.staffing} · </>}
                        {formatDate(r.created_at)}
                      </div>
                      {r.message && <p className="mt-1 text-white/75 whitespace-pre-line">{r.message}</p>}
                    </>
                  )}
                  {active === "contact" && (
                    <>
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="font-bold">{r.name}</span>
                        <a href={`mailto:${r.email}`} className="text-[#5FBDD6] hover:underline">{r.email}</a>
                      </div>
                      <div className="text-xs text-white/55 mt-0.5">{formatDate(r.created_at)}</div>
                      <p className="mt-1 text-white/75 whitespace-pre-line">{r.message}</p>
                    </>
                  )}
                  {active === "newsletter" && (
                    <>
                      <a href={`mailto:${r.email}`} className="font-bold text-[#5FBDD6] hover:underline">{r.email}</a>
                      <div className="text-xs text-white/55 mt-0.5">{formatDate(r.created_at)}</div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => remove(r.id)}
                  className="shrink-0 text-red-400 hover:text-red-300"
                  data-testid={`message-delete-${r.id}`}
                  aria-label="Delete entry"
                >
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}



// ============================================================
// PICKUP MENU EDITOR — set prices for the /order page
// ============================================================
function PickupItemsEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/pickup-items`);
        setItems(data);
      } catch (e) {
        toast.error("Couldn't load pickup items");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (item, patch) => {
    const next = { ...item, ...patch };
    setItems((arr) => arr.map((it) => (it.id === item.id ? next : it)));
    try {
      await axios.put(`${API}/admin/pickup-items/${item.id}`, patch);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  if (loading) return <FullScreenLoader />;

  const unpriced = items.filter((i) => i.price_cents <= 0).length;
  return (
    <div className="space-y-5">
      <SectionCard title="Pickup Menu" desc="Items, prices and prep times for the /order page. Customers can't check out items priced $0 — fill these in.">
        {unpriced > 0 && (
          <div className="mb-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30 px-3 py-2 text-xs text-yellow-200" data-testid="pickup-unpriced-banner">
            {unpriced} item{unpriced === 1 ? "" : "s"} still need a price.
          </div>
        )}
        <div className="grid gap-3">
          {items.map((it) => (
            <PickupRow key={it.id} item={it} onSave={(patch) => save(it, patch)} />
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function PickupRow({ item, onSave }) {
  const [draft, setDraft] = useState({
    name: item.name,
    desc: item.desc || "",
    price_dollars: ((item.price_cents || 0) / 100).toFixed(2),
    lead_hours: item.lead_hours || 2,
    img: item.img || "",
    active: item.active !== false,
  });

  const flush = () => {
    const cents = Math.max(0, Math.round(parseFloat(draft.price_dollars || "0") * 100));
    onSave({
      name: draft.name,
      desc: draft.desc,
      price_cents: cents,
      lead_hours: Number(draft.lead_hours) || 0,
      img: draft.img,
      active: draft.active,
    });
  };

  const unpriced = (parseFloat(draft.price_dollars || "0") || 0) <= 0;
  return (
    <div className={`rounded-2xl border ${unpriced ? "border-yellow-500/30" : "border-white/10"} bg-white/[0.03] p-3 flex gap-3 items-start`} data-testid={`pickup-row-${item.id}`}>
      {draft.img && <img src={draft.img} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />}
      <div className="flex-1 min-w-0 grid grid-cols-6 gap-2">
        <input
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          onBlur={flush}
          className="col-span-6 sm:col-span-3 rounded-md bg-white/[0.06] border border-white/15 text-white px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#5FBDD6]"
          placeholder="Name"
          data-testid={`pickup-row-name-${item.id}`}
        />
        <label className="col-span-3 sm:col-span-2 flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.16em] text-white/55 shrink-0">$</span>
          <input
            value={draft.price_dollars}
            onChange={(e) => setDraft({ ...draft, price_dollars: e.target.value })}
            onBlur={flush}
            inputMode="decimal"
            className={`w-full rounded-md bg-white/[0.06] border ${unpriced ? "border-yellow-500/40" : "border-white/15"} text-white px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#5FBDD6]`}
            placeholder="0.00"
            data-testid={`pickup-row-price-${item.id}`}
          />
        </label>
        <label className="col-span-3 sm:col-span-1 flex items-center gap-1.5">
          <input
            value={draft.lead_hours}
            onChange={(e) => setDraft({ ...draft, lead_hours: e.target.value })}
            onBlur={flush}
            inputMode="numeric"
            className="w-full rounded-md bg-white/[0.06] border border-white/15 text-white px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#5FBDD6]"
            placeholder="Hrs"
            data-testid={`pickup-row-lead-${item.id}`}
          />
          <span className="text-[10px] uppercase tracking-[0.16em] text-white/45">hrs</span>
        </label>
        <textarea
          value={draft.desc}
          onChange={(e) => setDraft({ ...draft, desc: e.target.value })}
          onBlur={flush}
          rows={1}
          className="col-span-6 rounded-md bg-white/[0.06] border border-white/15 text-white px-2.5 py-1.5 text-xs resize-none focus:outline-none focus:border-[#5FBDD6]"
          placeholder="Description"
        />
        <div className="col-span-6 flex items-center justify-between gap-2 pt-0.5">
          <label className="inline-flex items-center gap-2 text-[11px] text-white/65">
            <input type="checkbox" checked={draft.active} onChange={(e) => { setDraft({ ...draft, active: e.target.checked }); }} onBlur={flush} />
            Show on /order page
          </label>
          <span className="text-[10px] text-white/35">ID: {item.id}</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ORDERS PANEL — paid + pending pickup orders
// ============================================================
function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/orders`);
      setOrders(data);
    } catch (e) {
      toast.error("Couldn't load orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const setStatus = async (order_id, status) => {
    try {
      await axios.put(`${API}/admin/orders/${order_id}/status`, { fulfillment_status: status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const sendTestPrint = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/orders/test-print`);
      const printers = (data?.sent_to?.printers || []).join(", ") || "—";
      toast.success(`Test ticket sent to ${printers}`, {
        description: "Check the HP printer in ~30 seconds. If nothing prints, whitelist quotes@btwnyc.com in HP Smart → Print Anywhere → Allowed Senders.",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test print failed");
    }
  };

  if (loading) return <FullScreenLoader />;
  const paid = orders.filter((o) => o.payment_status === "paid");
  const other = orders.filter((o) => o.payment_status !== "paid");

  return (
    <div className="space-y-5">
      <SectionCard
        title="Auto-print kitchen tickets"
        desc="Every paid order is auto-emailed to your HP ePrint printer. Tap below to send a fake ticket and verify it prints."
      >
        <button
          type="button"
          onClick={sendTestPrint}
          data-testid="orders-send-test-print"
          className="px-4 py-2 text-sm font-bold uppercase tracking-wider bg-[#5FBDD6] text-black rounded-md hover:brightness-95 transition"
        >
          Send test print
        </button>
      </SectionCard>
      <SectionCard title="Paid pickup orders" desc="Orders that have been paid and need to be prepared / picked up.">
        {paid.length === 0 ? (
          <p className="text-white/55 text-sm">No paid orders yet.</p>
        ) : (
          <div className="grid gap-3">
            {paid.map((o) => <OrderRow key={o.order_id} o={o} onStatus={setStatus} />)}
          </div>
        )}
      </SectionCard>
      <SectionCard title="Other sessions" desc="Started but not completed — usually expired or abandoned. Safe to ignore.">
        {other.length === 0 ? (
          <p className="text-white/55 text-sm">No pending sessions.</p>
        ) : (
          <div className="grid gap-3">
            {other.map((o) => <OrderRow key={o.order_id || o.session_id} o={o} onStatus={setStatus} compact />)}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function OrderRow({ o, onStatus, compact = false }) {
  const total = `$${((o.amount_cents || 0) / 100).toFixed(2)}`;
  const status = o.fulfillment_status || "pending";
  const created = o.created_at ? new Date(o.created_at).toLocaleString() : "";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid={`order-row-${o.order_id || o.session_id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-display text-base uppercase tracking-tight">
          {o.customer_name || "—"} · <span style={{ color: "#5FBDD6" }}>{total}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-extrabold uppercase tracking-[0.18em] px-2 py-1 rounded-full ${o.payment_status === "paid" ? "bg-[#A3D900]/20 text-[#A3D900]" : "bg-white/10 text-white/55"}`}>
            {o.payment_status || "—"}
          </span>
          {o.payment_status === "paid" && (
            <span className={`text-[10px] font-extrabold uppercase tracking-[0.18em] px-2 py-1 rounded-full ${status === "pending" ? "bg-yellow-500/20 text-yellow-200" : status === "prepared" ? "bg-[#5FBDD6]/20 text-[#5FBDD6]" : status === "picked_up" ? "bg-white/15 text-white/75" : "bg-red-500/20 text-red-300"}`}>
              {status.replace("_", " ")}
            </span>
          )}
        </div>
      </div>
      {!compact && (
        <>
          <div className="mt-1 text-[11px] text-white/55 flex flex-wrap gap-x-3 gap-y-0.5">
            <span>📞 {o.customer_phone || "—"}</span>
            <span>🕒 Pickup: {o.pickup_at || "tbc"}</span>
            <span>📥 Placed: {created}</span>
          </div>
          <ul className="mt-2 text-xs text-white/75 space-y-0.5">
            {(o.items || []).map((li, i) => (
              <li key={i}>· {li.qty}× {li.name} — ${((li.unit_price_cents || 0) * li.qty / 100).toFixed(2)}</li>
            ))}
          </ul>
          {o.payment_status === "paid" && (
            <div className="mt-3 flex flex-wrap gap-2">
              {status !== "prepared" && status !== "picked_up" && (
                <button onClick={() => onStatus(o.order_id, "prepared")} data-testid={`order-mark-prepared-${o.order_id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-[#5FBDD6] text-[#0B0B0B] font-extrabold tracking-[0.14em] uppercase px-3 py-1.5 text-[10px]">
                  Mark prepared
                </button>
              )}
              {status !== "picked_up" && (
                <button onClick={() => onStatus(o.order_id, "picked_up")} data-testid={`order-mark-picked-up-${o.order_id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-[#A3D900] text-[#0B0B0B] font-extrabold tracking-[0.14em] uppercase px-3 py-1.5 text-[10px]">
                  Mark picked up
                </button>
              )}
              {status !== "cancelled" && (
                <button onClick={() => onStatus(o.order_id, "cancelled")} data-testid={`order-mark-cancelled-${o.order_id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] border border-white/15 text-white/70 hover:text-white font-bold tracking-[0.14em] uppercase px-3 py-1.5 text-[10px]">
                  Cancel
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
